import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useTheme, SPACING, TYPOGRAPHY } from '../../theme';
import { DotLabel, TextTabs } from '../common';
import { PostCategory } from '../../types';

interface ObservationTypeSelectorProps {
  observationType: PostCategory;
  onTypeChange: (type: PostCategory) => void;
}

const TYPES: { key: PostCategory; label: string; accessibilityLabel: string; hint: string }[] = [
  {
    key: 'progress',
    label: 'Progress',
    accessibilityLabel: 'Progress observation',
    hint: 'Work completed',
  },
  {
    key: 'risk',
    label: 'Opportunity',
    accessibilityLabel: 'Opportunity observation',
    hint: 'Something to fix',
  },
  {
    key: 'news',
    label: 'News',
    accessibilityLabel: 'News post',
    hint: 'Tell your community what is happening in wildfire preparation',
  },
];

/**
 * Plaud grammar: the two boxed, tinted cards become underlined text tabs.
 * Category colour is still information — it moves to the dot-label under the
 * tabs, where it names the choice instead of shouting it as a fill.
 */
export function ObservationTypeSelector({
  observationType,
  onTypeChange,
}: ObservationTypeSelectorProps) {
  const { colors } = useTheme();
  const active = TYPES.find((t) => t.key === observationType) ?? TYPES[0];

  return (
    <View style={styles.section}>
      <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>Observation Type</Text>
      <TextTabs
        mode="radiogroup"
        tabs={TYPES}
        activeKey={observationType}
        onChange={(key) => onTypeChange(key as PostCategory)}
      />
      <View style={styles.summary}>
        <DotLabel label={active.label} variant={active.key} />
        <Text style={[styles.hint, { color: colors.textSecondary }]}>{active.hint}</Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  section: {
    marginBottom: SPACING.lg,
  },
  sectionLabel: {
    ...TYPOGRAPHY.label,
    marginBottom: SPACING.sm,
  },
  summary: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    marginTop: SPACING.smd,
  },
  hint: {
    ...TYPOGRAPHY.bodySmall,
  },
});
